import { useForm, Controller } from "react-hook-form";
import { Box, Button, NumberInput, Divider, FileButton, Flex, Group, Select, TextInput, SimpleGrid, Stack, Text} from "@mantine/core";
import { IconChevronDown } from "@tabler/icons-react";
import countries from "../../../../data/countries.json";
import { errorNotifications } from "../../../../utils/handleNotifications";

const countrySelectData = countries.map((item) => ({
  value: item.id.toString(),
  label: item.name,
}));

const AddOperatorForm = ({ onSubmit, isSubmitting = false }) => {
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    defaultValues: {
      operator_type: "",
      operator_name: "",
      email: "",
      phone_number: "",
      country: "",
      office_address: "",
      poc_name: "",
      logo: null,
      designation: "Operator",
      permission: "Operator",
      department_name: "Operators",
    },
  });
  const selectedLogo = watch("logo");

  const handleInvalidSubmit = (formErrors) => {
    const firstError = Object.values(formErrors).find((error) => error?.message);
    errorNotifications(
      {
        form: [
          firstError?.message || "Please fill in all required fields.",
        ],
      },
      { response: { status: 400, statusText: "Validation" } }
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)} noValidate>
      <Box>
        <Flex h={62} pos="relative" justify="center" align="center">
          <Text c="#092C4C" fz={24} fw={500} lh="normal" ta="center">
            + Add New Operator
          </Text>
        </Flex>

        <Divider color="rgba(0,83,168,0.15)" />

        <Box px={32} pt={32} pb={64}>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={80} verticalSpacing={36}>
            <Stack gap={12}>
              <Text c="#092C4C" fz={14} fw={500}>
                Operator Type
              </Text>
              <Controller
                name="operator_type"
                control={control}
                rules={{ required: "Operator type is required" }}
                render={({ field }) => (
                  <Select
                    placeholder="Select operator type"
                    data={["Domestic", "International"]}
                    value={field.value || null}
                    onChange={field.onChange}
                    error={errors.operator_type?.message}
                    allowDeselect={false}
                    h={48}
                    rightSection={<IconChevronDown size={14} color="#6C94B4" />}
                  />
                )}
              />
            </Stack>

            <Stack gap={12}>
              <Text c="#092C4C" fz={14} fw={500}>
                Name
              </Text>
              <TextInput
                placeholder="Enter operator name"
                h={48}
                error={errors.operator_name?.message}
                {...register("operator_name", {
                  required: "Operator name is required",
                  validate: (value) =>
                    value.trim().length > 0 || "Operator name cannot be empty",
                })}
              />
            </Stack>

            <Stack gap={12}>
              <Text c="#092C4C" fz={14} fw={500}>
                Email
              </Text>
              <TextInput
                type="email"
                placeholder="Enter email address"
                h={48}
                error={errors.email?.message}
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Enter a valid email address",
                  },
                })}
              />
            </Stack>

            <Stack gap={12}>
              <Text c="#092C4C" fz={14} fw={500}>
                Phone Number
              </Text>
              <Controller
                name="phone_number"
                control={control}
                rules={{
                  required: "Phone number is required",
                  validate: (value) =>
                    String(value || "").length >= 10 &&
                    String(value || "").length <= 15
                      ? true
                      : "Phone number must be between 10 and 15 digits",
                }}
                render={({ field }) => (
                  <NumberInput
                    placeholder="Enter phone number"
                    hideControls
                    allowDecimal={false}
                    allowNegative={false}
                    clampBehavior="none"
                    error={errors.phone_number?.message}
                    {...field}
                  />
                )}
              />
            </Stack>

            <Stack gap={12}>
              <Text c="#092C4C" fz={14} fw={500}>
                Country
              </Text>
              <Controller
                name="country"
                control={control}
                rules={{ required: "Country is required" }}
                render={({ field }) => (
                  <Select
                    placeholder="Select country"
                    searchable
                    data={countrySelectData}
                    value={field.value || null}
                    onChange={field.onChange}
                    error={errors.country?.message}
                    allowDeselect={false}
                    h={48}
                    rightSection={<IconChevronDown size={14} color="#6C94B4" />}
                  />
                )}
              />
            </Stack>

            <Stack gap={12}>
              <Text c="#092C4C" fz={14} fw={500}>
                Office Address
              </Text>
              <TextInput
                type="text"
                placeholder="Enter office address"
                h={48}
                error={errors.office_address?.message}
                {...register("office_address", { required: "Office address is required" })}
              />
            </Stack>

            <Stack gap={12}>
              <Text c="#092C4C" fz={14} fw={500}>
                Upload Logo
              </Text>
              <Group gap={24} wrap="nowrap" align="center">
                <FileButton onChange={(file) => setValue("logo", file)}>
                  {(props) => (
                    <Button
                      {...props}
                      variant="light"
                      color="gray"
                      radius={8}
                      h={48}
                      w={248}
                      styles={{
                        inner: { justifyContent: "flex-start" },
                        label: {
                          color: "#6C94B4",
                          fontSize: 14,
                          fontWeight: 400,
                        },
                      }}
                    >
                      Choose File
                    </Button>
                  )}
                </FileButton>
                <Text c="#6C94B4" fz={14} fw={500} lh="18px">
                  {selectedLogo ? selectedLogo.name : "No file chosen"}
                </Text>
              </Group>
            </Stack>

            <Stack gap={12}>
              <Text c="#092C4C" fz={14} fw={500}>
                POC Person
              </Text>
              <TextInput
                type="text"
                placeholder="Enter point of contact name"
                h={48}
                error={errors.poc_name?.message}
                {...register("poc_name", { required: "POC person is required" })}
              />
            </Stack>
          </SimpleGrid>

          <input type="hidden" {...register("designation")} />
          <input type="hidden" {...register("permission")} />
          <input type="hidden" {...register("department_name")} />
        </Box>

        <Divider color="rgba(0,83,168,0.15)" />

        <Flex justify="center" py={32}>
          <Button
            type="submit"
            variant="gradient"
            gradient={{ from: "#0053A8", to: "#033F7C", deg: 270 }}
            h={48}
            w={156}
            radius={8}
            fz={16}
            fw={700}
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            Submit
          </Button>
        </Flex>
      </Box>
    </form>
  );
};

export default AddOperatorForm;